import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { environmentSelector } from 'src/app/store/global/global.selectors';
import { ILog } from 'src/app/interfaces/logcenter/logcenter.interface';
import { ConfigurationType, IConfiguration } from '@bciti/types';

export interface PatchConfigurationPayload<T = any> {
    configuration: {
        key: ConfigurationType;
        data: T;
    };
}

@Injectable({
    providedIn: 'root',
})
export class ConfigurationService {
    constructor(private http: HttpClient, private store: Store<State>) {}

    getConfigs(): Observable<IConfiguration<any>[]> {
        return this.store.select(environmentSelector).pipe(
            switchMap((env) =>
                this.http.get<IConfiguration<any>[]>(`${env.baseUrl}/cerberus/configurations`).pipe(
                    map((configurations) => configurations.sort((a, b) => (a.organisation[0] > b.organisation[0] ? 1 : -1))),
                    tap((_) => this.log('Configs Fetched')),
                    catchError(this.handleError<IConfiguration<any>[]>('getConfigs', [])),
                ),
            ),
        );
    }

    getConfigurationByKey(organisation: string, key: string): Observable<any> {
        return this.store.select(environmentSelector).pipe(
            switchMap((env) =>
                this.http.get<any>(`${env.baseUrl}/cerberus/configurations/${key}`, {
                    headers: { 'x-organisation': organisation },
                }),
            ),
        );
    }

    getModules(): Observable<any> {
        return this.store.select(environmentSelector).pipe(switchMap((env) => this.http.get<any>(`${env.baseUrl}/cerberus/logs/modules`)));
    }

    getTypes(): Observable<any> {
        return this.store.select(environmentSelector).pipe(switchMap((env) => this.http.get<any>(`${env.baseUrl}/cerberus/logs/types`)));
    }

    getLogs(queryString): Observable<ILog[]> {
        return this.store.select(environmentSelector).pipe(switchMap((env) => this.http.get<any>(`${env.baseUrl}/cerberus/logs?${queryString}`)));
    }

    getLastDaysModulesErrors(queryString): Observable<ILog[]> {
        return this.store.select(environmentSelector).pipe(switchMap((env) => this.http.get<any>(`${env.baseUrl}/cerberus/logs/lastDaysModulesErrors?${queryString}`)));
    }

    getContentsByKey(organisation: string, key: string): Observable<any> {
        return this.store.select(environmentSelector).pipe(
            switchMap((env) =>
                this.http.get<any>(`${env.baseUrl}/cerberus/contents/${key}`, {
                    headers: { 'x-organisation': organisation },
                }),
            ),
        );
    }

    patchContents(organisation: string, key: string, data: object): Observable<any> {
        return this.store.select(environmentSelector).pipe(
            switchMap((env) =>
                this.http.patch<any>(`${env.baseUrl}/cerberus/contents/${key}`, data, {
                    headers: { 'x-organisation': organisation },
                }),
            ),
        );
    }

    createConfiguration<T = any, U = any>(organisation: string, data: PatchConfigurationPayload<T>): Observable<U> {
        return this.store.select(environmentSelector).pipe(
            switchMap((env) =>
                this.http.post<U>(`${env.baseUrl}/cerberus/configurations`, data, {
                    headers: { 'x-organisation': organisation },
                }),
            ),
        );
    }

    patchConfigurations(organisation: string, data: PatchConfigurationPayload): Observable<any> {
        return this.store.select(environmentSelector).pipe(
            switchMap((env) =>
                this.http.patch<any>(`${env.baseUrl}/cerberus/configurations/`, data, {
                    headers: { 'x-organisation': organisation },
                }),
            ),
        );
    }

    private log(message: string) {
        // this.messageService.add(`Configurations : ${message}`);
    }

    compareBranches(queryString): Observable<any[]> {
        return this.store.select(environmentSelector).pipe(switchMap((env) => this.http.get<any>(`${env.baseUrl}/cerberus/compareBranches?${queryString}`)));
    }

    getDeploymentsInfos(): Observable<any[]> {
        return this.store.select(environmentSelector).pipe(switchMap((env) => this.http.get<any>(`${env.baseUrl}/cerberus/deploymentsInfos`)));
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {
            // TODO: send the error to remote logging infrastructure
            console.error(error); // log to console instead

            // TODO: better job of transforming error for user consumption
            this.log(`${operation} failed: ${error.message}`);

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }
}
